<!-- Recursive group template -->
<ng-template #groupTemplate let-group="group" let-depth="depth" let-isRoot="isRoot">

  <!-- EMPTY STATE (when root has no children) -->
  <div *ngIf="isRoot && isRootEmpty()" class="flex flex-column align-items-center py-5 gap-2">
    <i class="fa-regular fa-filter-slash text-3xl text-color-secondary opacity-30"></i>
    <span class="font-bold text-color-secondary">Noch keine Filter</span>
    <span class="text-sm text-color-secondary">Füge unten Bedingungen hinzu. Verwende Gruppe hinzufügen um AND/OR-Logik beliebig zu verschachteln.</span>
  </div>

  <!-- GROUP HEADER (not for root) -->
  <div *ngIf="!isRoot" class="flex align-items-center justify-content-between w-full px-2 pt-2 pb-1">
    <div class="flex align-items-center gap-2">
      <div class="inline-flex overflow-hidden flex-shrink-0 shadow-1" style="border-radius: 9px; border: 1.5px solid var(--surface-300);">
        <button pButton class="logic-pill-option" [class.active]="isAndGroup(group)" [class.and-option]="isAndGroup(group)" (click)="toggleGroupLogic(group)">AND</button>
        <button pButton class="logic-pill-option" [class.active]="!isAndGroup(group)" [class.or-option]="!isAndGroup(group)" (click)="toggleGroupLogic(group)">OR</button>
      </div>
      <span class="text-color-secondary white-space-nowrap" style="font-size: 11px;" *ngIf="getGroupChildCount(group) >= 2">
        <ng-container *ngIf="isAndGroup(group)"><b class="lw-and">Alle</b> Bedingungen müssen zutreffen</ng-container>
        <ng-container *ngIf="!isAndGroup(group)"><b class="lw-or">Eine</b> Bedingung muss zutreffen</ng-container>
      </span>
      <span class="text-xs text-color-secondary font-medium">Gruppe · {{ getGroupChildCount(group) }} {{ getGroupChildCount(group) !== 1 ? 'Einträge' : 'Eintrag' }}</span>
    </div>
    <button pButton type="button" icon="pi pi-times" class="p-button-text c-del-btn flex-shrink-0" (click)="deleteGroup(group)" title="Gruppe entfernen"></button>
  </div>

  <!-- ROOT LEVEL: only AND/OR pill toggle (no inline sentence, no "?" button) when 2+ children -->
  <div *ngIf="isRoot && getGroupChildCount(group) >= 2" class="flex align-items-center gap-2 mb-2">
    <div class="inline-flex overflow-hidden flex-shrink-0 shadow-1" style="border-radius: 9px; border: 1.5px solid var(--surface-300);">
      <button pButton class="logic-pill-option" [class.active]="isAndGroup(group)" [class.and-option]="isAndGroup(group)" (click)="toggleGroupLogic(group)">AND</button>
      <button pButton class="logic-pill-option" [class.active]="!isAndGroup(group)" [class.or-option]="!isAndGroup(group)" (click)="toggleGroupLogic(group)">OR</button>
    </div>
  </div>

  <!-- ALL CHILDREN (conditions + groups in natural order) -->
  <div [class]="isRoot ? '' : 'px-3 pb-1'">
    <ng-container *ngFor="let child of getGroupChildren(group); let i = index">

      <!-- CONDITION ROW -->
      <div *ngIf="child.uiType === 0"
           class="c-row py-2 w-full"
           [attr.data-criteria-label]="child.label"
           [ngClass]="{'validation-error-highlight': isCriteriaHighlighted(child)}">
       <div class="flex align-items-center gap-3 w-full">

        <!-- WHERE/AND/OR connector -->
        <span class="text-right pr-2 text-xs font-bold text-color-secondary flex-shrink-0" style="min-width: 3rem;">{{ i === 0 ? (isRoot ? 'WHERE' : '') : (isAndGroup(group) ? 'AND' : 'OR') }}</span>

        <!-- p-treeSelect for field (FIX 1: min-width so labels are readable) -->
        <p-treeSelect
          class="flex-shrink-0"
          [ngClass]="{'cb-field-warning': isFieldMissing(child)}"
          [style]="{'min-width': '18rem', 'background-color': 'var(--blue-50)'}"
          [options]="cascadeNodes"
          (onNodeExpand)="onNodeExpand($event)"
          (onNodeSelect)="onTreeSelectNodeSelect(child, 'attributeItems', $event)"
          [ngModel]="child.field ? child : null"
          [filter]="true" [filterInputAutoFocus]="true"
          optionLabel="label"
          placeholder="Attribut wählen"
          appendTo="body">
        </p-treeSelect>

        <!-- Operation chip -->
        <span
                [ngClass]="child?.operation
                  ? 'flex-shrink-0 inline-flex align-items-center justify-content-center cursor-pointer border-round white-space-nowrap bg-primary text-white font-semibold cb-op-chip'
                  : 'flex-shrink-0 inline-flex align-items-center justify-content-center cursor-pointer border-round white-space-nowrap surface-100 text-color-secondary border-1 surface-border cb-op-chip'"
                [class.cb-op-warning]="isOperationMissing(child)"
                [style]="{'width': '9rem', 'height': '2.25rem', 'font-size': '0.8125rem', 'padding': '0 8px'}"
                (click)="togglePopupMenu(child, operationItems, $event,'operationItems')">
          <ng-container *ngIf="child?.operation">
            {{ getOperationLabel(child.operation) }}
          </ng-container>
          <ng-container *ngIf="!child?.operation">
            <i class="fa-regular fa-circle-question text-sm mr-1"></i>
            <span class="text-xs">Operator</span>
          </ng-container>
        </span>

        <!-- Value inputs (only when operation is NOT IS_NULL/IS_NOT_NULL) -->
        <ng-container *ngIf="child?.operation !== 'IS_NULL' && child?.operation !== 'IS_NOT_NULL'">

          <!-- manual text input (string type) -->
          <input type="text" pInputText [(ngModel)]="child.value"
                 class="flex-1"
                 style="min-width: 10rem; max-width: 20rem"
                 [ngClass]="{'cb-value-warning': isValueMissing(child)}"
                 placeholder="Wert eingeben"
                 *ngIf="child.dataType == filterAttributeDateTypeEnum.string && child.selectManual && !child.isJoin"
                 (keyup)="handleChange()"/>

          <!-- manual number input -->
          <p-inputNumber inputId="integeronly" [(ngModel)]="child.value"
                         class="flex-1"
                         style="min-width: 10rem; max-width: 20rem"
                         [inputStyle]="{ width: '100%' }"
                         [ngClass]="{'cb-value-warning': isValueMissing(child)}"
                         *ngIf="(child.dataType == filterAttributeDateTypeEnum.number_integer || child.dataType == filterAttributeDateTypeEnum.number_decimal) && child.selectManual"
                         [maxFractionDigits]="child.dataType == filterAttributeDateTypeEnum.number_integer ? 0 : 2"
                         (onBlur)="handleChange()">
          </p-inputNumber>

          <!-- Date field: date option dropdown -->
          <p-select class="flex-shrink-0"
                    style="width: 12rem"
                    [placeholder]="'Datum-Option'"
                    [options]="dateFilterOptions"
                    [(ngModel)]="child.selectedDateOption"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    *ngIf="child.isDateField"
                    (onChange)="onDateOptionSelect(child)" appendTo="body">
          </p-select>

          <!-- Date field: date picker for specific options (specificDay, specificMonth, specificYear, dateRange) -->
          <p-datePicker
            [(ngModel)]="child.dateValue"
            placeholder="Datum wählen"
            class="flex-shrink-0"
            style="width: 12rem"
            [ngClass]="{'cb-value-warning': isValueMissing(child)}"
            *ngIf="child.isDateField && child.selectManual"
            [showTime]="child.dataType == filterAttributeDateTypeEnum.dateTime || child.dataType == filterAttributeDateTypeEnum.dateInstant"
            dateFormat="dd.mm.yy"
            (onSelect)="handleDate(child, 'value')"
            (onBlur)="handleDate(child, 'value')"
          ></p-datePicker>

          <!-- join search dropdown -->
          <mvs-search-dropdown
            class="flex-1"
            style="min-width: 10rem; max-width: 20rem"
            *ngIf="child.isJoin && child.joinObjectTypeAlias"
            [readOnly]="readOnly"
            [selectedObject]="child.cascadeValueSelect ? child.cascadeValueSelect : child.value ? {id: child.value, name: null} : null"
            [objectTypeAlias]="[child.joinObjectTypeAlias]"
            (onSelect)="handleSelectObjectSearch(child, $event)">
          </mvs-search-dropdown>

          <!-- manual boolean select (real booleans only, not enums or dates) -->
          <p-selectButton class="flex-shrink-0"
                *ngIf="child.dataType == filterAttributeDateTypeEnum.boolean && !child.isEnumField && !child.isDateField && child.selectManual"
                [options]="booleanOptions"
                [(ngModel)]="child.value"
                optionLabel="label"
                optionValue="value"
                (onChange)="handleChange()">
          </p-selectButton>

          <!-- Enum field: simple dropdown with enum values directly -->
          <p-select class="flex-1"
                    style="min-width: 10rem; max-width: 20rem"
                    [ngClass]="{'cb-value-warning': isValueMissing(child)}"
                    [placeholder]="'Wert wählen'"
                    [options]="getEnumItems(child)"
                    [(ngModel)]="child.cascadeValueSelect"
                    optionLabel="label"
                    [filter]="getEnumItems(child).length > 8"
                    filterBy="label"
                    [showClear]="true"
                    *ngIf="child.selectManual == false && child.isEnumField"
                    (onChange)="onEnumSelect(child)" appendTo="body">
          </p-select>


          <!-- parameter value input for first value -->
          <p-inputNumber inputId="integeronly" [(ngModel)]="child.parameterValue"
                         class="flex-shrink-0"
                         [style]="{ width: '5rem' }"
                         [inputStyle]="{ width: '5rem' }"
                         *ngIf="child.hasParameter"
                         [maxFractionDigits]="child.dataType == filterAttributeDateTypeEnum.number_integer ? 0 : 2"
                         (onBlur)="handleVariableParameter(child,'value')">
          </p-inputNumber>

          <!-- BT (Between) second value inputs -->
          <input type="text" pInputText [(ngModel)]="child.valueTo"
                 class="w-15rem"
                 placeholder="Wert eingeben"
                 *ngIf="child.dataType == filterAttributeDateTypeEnum.string && child.selectManualValueTo && child?.operation=='BT'"
                 (keyup)="handleChange()"/>

          <p-inputNumber inputId="integeronly" [(ngModel)]="child.valueTo"
                         class="w-15rem"
                         [inputStyle]="{ width: '100%' }"
                         *ngIf="(child.dataType == filterAttributeDateTypeEnum.number_integer || child.dataType == filterAttributeDateTypeEnum.number_decimal) && child.selectManualValueTo && child?.operation=='BT'"
                         [maxFractionDigits]="child.dataType == filterAttributeDateTypeEnum.number_integer ? 0 : 2"
                         (onBlur)="handleChange()">
          </p-inputNumber>

          <!-- Date field: second date picker for dateRange BT mode -->
          <p-datePicker [(ngModel)]="child.dateValueTo"
                      placeholder="Datum wählen"
                      class="flex-shrink-0"
                      style="width: 12rem"
                      *ngIf="child.isDateField && child.selectManualValueTo && child?.operation=='BT'"
                      [showTime]="child.dataType == filterAttributeDateTypeEnum.dateTime || child.dataType == filterAttributeDateTypeEnum.dateInstant"
                      dateFormat="dd.mm.yy"
                      (onSelect)="handleDate(child, 'valueTo')"
                      (onBlur)="handleDate(child, 'valueTo')"></p-datePicker>

          <!-- Boolean select for BT mode (real booleans only, not enums) -->
          <p-selectButton class="flex-shrink-0"
                *ngIf="child.dataType == filterAttributeDateTypeEnum.boolean && !child.isEnumField && !child.isDateField && child.selectManualValueTo && child?.operation=='BT'"
                [options]="booleanOptions"
                [(ngModel)]="child.valueTo"
                optionLabel="label"
                optionValue="value"
                (onChange)="handleChange()">
          </p-selectButton>

          <!-- Enum field: simple dropdown for second value (BT mode) -->
          <p-select class="w-15rem"
                    [placeholder]="'Wert wählen'"
                    [options]="getEnumItems(child)"
                    [(ngModel)]="child.cascadeValueToSelect"
                    optionLabel="label"
                    [filter]="getEnumItems(child).length > 8"
                    filterBy="label"
                    [showClear]="true"
                    *ngIf="child.isEnumField && child?.operation=='BT'"
                    (onChange)="onEnumSelect(child)" appendTo="body">
          </p-select>

          <!-- parameter value input for second value (BT mode) -->
          <p-inputNumber inputId="integeronly" [(ngModel)]="child.parameterValueTo"
                         class="flex-shrink-0"
                         [style]="{ width: '5rem' }"
                         [inputStyle]="{ width: '5rem' }"
                         *ngIf="child.hasParameterValueTo && child?.operation=='BT'"
                         [maxFractionDigits]="child.dataType == filterAttributeDateTypeEnum.number_integer ? 0 : 2"
                         (onBlur)="handleVariableParameter(child , 'valueTo')">
          </p-inputNumber>

          <!-- Fallback: disabled value input when no field is selected yet -->
          <input *ngIf="!child.field" type="text" pInputText
                 [disabled]="true" placeholder="Wert eingeben"
                 style="min-width: 10rem; max-width: 20rem; opacity: 0.5;" />

        </ng-container>

        <!-- Delete button -->
        <button pButton type="button" icon="pi pi-times"
                class="p-button-text c-del-btn flex-shrink-0 ml-auto" (click)="onRemoveCriteria(child)"></button>
       </div>
        <!-- Inline validation message -->
        <div *ngIf="isConditionIncomplete(child)" class="cb-inline-validation flex align-items-center gap-1 mt-1" style="padding-left: 4.5rem;">
          <i class="fa-regular fa-circle-exclamation text-red-500" style="font-size: 11px;"></i>
          <span class="text-xs text-red-500">
            <ng-container *ngIf="isFieldMissing(child)">Attribut fehlt</ng-container>
            <ng-container *ngIf="!isFieldMissing(child) && isOperationMissing(child)">Operator fehlt</ng-container>
            <ng-container *ngIf="!isFieldMissing(child) && !isOperationMissing(child) && isValueMissing(child)">Wert fehlt</ng-container>
          </span>
        </div>
      </div>

      <!-- SUB-GROUP (recursive) -->
      <div *ngIf="child.uiType === 1" class="cb-nested-group" [ngClass]="getGroupDepthClass(depth + 1, child)" [style]="'margin-left: 12px; border-left: 3px solid ' + (isAndGroup(child) ? 'var(--green-400)' : 'var(--orange-400)') + '; border-top: 1.5px solid ' + (isAndGroup(child) ? 'var(--green-200)' : 'var(--orange-200)') + '; border-right: 1.5px solid ' + (isAndGroup(child) ? 'var(--green-200)' : 'var(--orange-200)') + '; border-bottom: 1.5px solid ' + (isAndGroup(child) ? 'var(--green-200)' : 'var(--orange-200)') + '; background: ' + (isAndGroup(child) ? 'var(--green-50)' : 'var(--yellow-50)') + '; border-radius: 8px; padding: 8px; margin: 6px 0;'">
        <ng-container *ngTemplateOutlet="groupTemplate; context: {group: child, depth: depth + 1, isRoot: false}">
        </ng-container>
      </div>

    </ng-container>
  </div>

  <!-- ADD BUTTONS (always visible) -->
  <div *ngIf="!isRootEmpty() || !isRoot" class="flex gap-2 py-2" [class]="isRoot ? '' : 'px-3'">
    <span style="border: 1.5px dashed var(--surface-400); border-radius: 20px; background: transparent; color: var(--text-color-secondary); padding: 5px 14px; font-size: 11px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;"
          (click)="addConditionToGroup(group)">
      <i class="fa-regular fa-plus" style="font-size: 10px;"></i> Bedingung hinzufügen
    </span>
    <span style="border: 1.5px dashed var(--primary-200); border-radius: 20px; background: transparent; color: var(--primary-color); padding: 5px 14px; font-size: 11px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;"
          (click)="addSubGroupToGroup(group)">
      <i class="fa-regular fa-folder-plus" style="font-size: 10px;"></i> Untergruppe hinzufügen
    </span>
  </div>

</ng-template>

<!-- MAIN RENDER: invoke template with root -->
<ng-container *ngIf="filterCriteriaUiRoot">
  <ng-container *ngTemplateOutlet="groupTemplate; context: {group: filterCriteriaUiRoot, depth: 0, isRoot: true}">
  </ng-container>
</ng-container>

<!-- Keep the existing p-menu for operations popup -->
<p-menu [style]="{maxHeight:'20rem', overflow:'auto'}" #popupMenu [popup]="true" [model]="popupMenuItems"
        (onHide)="onAttributeItemsSelect()" appendTo="body"
></p-menu>

